import React, {useMemo, useRef, useState} from 'react';
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/system/useMediaQuery";
import {Box, Chip, CircularProgress, Input, Typography, useTheme} from "@mui/joy";
import {Button} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import _ from "lodash";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {PersonAddRounded, SearchRounded} from "@mui-symbols-material/w300";
import {BasicModalContent} from "@dreebit-registry/vsm-microservice-components";
import {ContactPersonPickerList} from "../../graphql/queries/contactPersonPickerList";
import {AssignContactPersonToInstalledBaseNode} from "../../graphql/mutations/assignContactPersonToInstalledBaseNode";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import ContactPersonFormModal from "./ContactPersonFormModal";
import {ContactPersonInfo} from "../../utils/contactAssignments";

const PICKER_LIMIT = 25;

interface Props {
    open: boolean;
    onClose: () => void;
    nodeId: string;
    // the node owner's client — a node only accepts its own client's contacts
    ownerId: string;
    // persons already directly assigned; their picker rows are disabled
    assignedPersonIds: string[];
}

/**
 * "Add contact" flow of the node contact tab: picks one of the client's existing
 * contacts to assign (preventing duplicate records), with an inline switch to the
 * create form — a newly created person is assigned to the node right away.
 */
const AddContactModal = ({open, onClose, nodeId, ownerId, assignedPersonIds}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const [step, setStep] = useState<'pick' | 'create'>('pick');
    const [searchQuery, setSearchQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [assigningId, setAssigningId] = useState<string | null>(null);

    const debouncedSearch = useRef(_.debounce((value: string) => setSearchQuery(value), 400)).current;

    const pickerQuery = useQuery(ContactPersonPickerList, {
        variables: {
            filter: {clientId: {equals: ownerId}},
            ...(searchQuery ? {search: {query: searchQuery, fields: []}} : {}),
            pagination: {start: 0, limit: PICKER_LIMIT},
        },
        fetchPolicy: "cache-and-network",
        skip: step !== 'pick',
    });

    const persons: ContactPersonInfo[] = _.get(pickerQuery, 'data.contactPersonList.contactPersons', []) ?? [];
    const total = _.get(pickerQuery, 'data.contactPersonList.total', 0);
    const isInitialLoading = pickerQuery.loading && !pickerQuery.data;
    const assignedIds = useMemo(() => new Set(assignedPersonIds), [assignedPersonIds]);

    const [assignMutation] = useMutation(AssignContactPersonToInstalledBaseNode);

    const assignPerson = async (personId: string, personName: string) => {
        setAssigningId(personId);
        try {
            await assignMutation({
                variables: {contactPersonId: personId, installedBaseNodeId: nodeId},
                refetchQueries: ['InstalledBaseNodeContactPersonList'],
            });
            toast.success(t('Contact "{{name}}" assigned.', {name: personName}));
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        } finally {
            setAssigningId(null);
        }
    };

    const onPersonCreated = (resultData: any) => {
        const personId = _.get(resultData, 'createContactPerson.id');
        const personName = _.get(resultData, 'createContactPerson.name');
        if (personId) {
            assignPerson(String(personId), personName ?? '');
        } else {
            onClose();
        }
    };

    if (step === 'create') {
        return (
            <ContactPersonFormModal
                mode="create"
                clientId={ownerId}
                open={open}
                onClose={onClose}
                onSuccess={onPersonCreated}
                refetchQueries={['ContactPersonPickerList']}
            />
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isFullScreen}
            slotProps={{
                paper: {sx: {minWidth: isFullScreen ? undefined : "600px"}},
            }}
        >
            <BasicModalContent
                startIcon={<PersonAddRounded/>}
                onClose={onClose}
                title={<Typography level="titleSmallLight">{t('Add contact')}</Typography>}
            >
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', pt: '8px'}} data-testid="contact-picker">
                    <Input
                        autoFocus
                        startDecorator={<SearchRounded/>}
                        placeholder={t('Search contacts')}
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            debouncedSearch(e.target.value);
                        }}
                        sx={{background: 'transparent'}}
                        data-testid="contact-picker-search"
                    />

                    <Box sx={{maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px'}}>
                        {isInitialLoading ? (
                            <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                                <CircularProgress variant="soft" size="sm"/>
                            </Box>
                        ) : persons.length === 0 ? (
                            <Typography level="body-sm" sx={{color: 'text.secondary', textAlign: 'center', p: 2}}>
                                {searchQuery
                                    ? t('No contacts found.')
                                    : t('This company has no contacts yet.')}
                            </Typography>
                        ) : (
                            persons.map((person) => {
                                const alreadyAssigned = assignedIds.has(person.id);
                                const isAssigning = assigningId === person.id;
                                return (
                                    <Box
                                        key={person.id}
                                        data-testid="contact-picker-row"
                                        role={alreadyAssigned ? undefined : 'button'}
                                        onClick={alreadyAssigned || assigningId ? undefined : () => assignPerson(person.id, person.name)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            p: '8px 12px',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: '8px',
                                            opacity: alreadyAssigned ? 0.55 : 1,
                                            ...(!alreadyAssigned && {
                                                cursor: 'pointer',
                                                '&:hover': {borderColor: 'primary.500'},
                                            }),
                                        }}
                                    >
                                        <Box sx={{flex: 1, minWidth: 0}}>
                                            <Typography level="bodyBold" noWrap>{person.name}</Typography>
                                            <Typography level="caption" noWrap sx={{color: '#666666'}}>
                                                {[person.role, person.email].filter(Boolean).join(' · ')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{flexShrink: 0}}>
                                            {isAssigning ? (
                                                <CircularProgress size="sm" variant="soft"/>
                                            ) : alreadyAssigned ? (
                                                <Chip size="sm" color="neutral" variant="soft">
                                                    {t('Already assigned')}
                                                </Chip>
                                            ) : null}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                        {!isInitialLoading && total > PICKER_LIMIT && (
                            <Typography level="caption" sx={{color: '#666666', textAlign: 'center'}}>
                                {t('Showing the first {{count}} contacts — refine the search.', {count: PICKER_LIMIT})}
                            </Typography>
                        )}
                    </Box>

                    {/* Centered under the empty-state message; left-aligned with results. */}
                    <Box sx={{display: 'flex', justifyContent: !isInitialLoading && persons.length === 0 ? 'center' : 'flex-start'}}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PersonAddRounded sx={{fontSize: 18}}/>}
                            onClick={() => setStep('create')}
                            data-testid="contact-picker-create"
                        >
                            {t('Create new contact')}
                        </Button>
                    </Box>
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default AddContactModal;
